
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { Box, InputAdornment, Typography } from '@mui/material';
import {
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedTable,
    GroupedTableRow,
    ICrfConditionParameters,
    IGroupedTableCellProps,
    IGroupedTableHeaderProps,
    Input,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { styled } from '@mui/material';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import GroupedExpandableField from '../fields/GroupedExpandableField';
import SelectWithTooltip from '../inputs/SelectWithTooltip';
import { NumberFormatValues } from 'react-number-format';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IHistopathologyFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
    
}))


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const tumourHistopathologyTypeSpecify = ({ formState }: ICrfConditionParameters<Dtos.Histopathology>) => formState?.value?.tumourHistopathologyType == Dtos.LookupHistopathologyType.Other;
const closestSuperficialMarginLessThanTwo = ({ formState }: ICrfConditionParameters<Dtos.Histopathology>) => (formState?.value?.closestSuperficialMarginValue ?? 2) < 2 || 
    ((formState?.value?.closestSuperficialMarginValue ?? 0) == 2 && Dtos.LookupLessGreaterThanType.LessThan === formState?.value?.closestSuperficialMarginOperator);

// const dcisReported = ({ formState }: ICrfConditionParameters<Dtos.Histopathology>) => (formState?.value?.dcis ?? 0) === Dtos.LookupDCISType.PresentEIC || (formState?.value?.dcis ?? 0) === Dtos.LookupDCISType.PresentNotEIC;

const ki67AssessmentDone = ({ formState }: ICrfConditionParameters<Dtos.Histopathology>) => (formState?.value?.ki67NotDone ?? false) === false;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const hormoneReceptorStatusHeader: IGroupedTableHeaderProps[] = [
    {
        columnName: 'name',
        content: '',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'percentage',
        content: 'Symbol',
        TableCellProps: {
            sx: {
                width: '10%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'percentageValue',
        content: 'Percentage Nuclei Stained',
        TableCellProps: {
            sx: {
                width: '20%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'intensity',
        content: 'Staining Intensity',
        TableCellProps: {
            sx: {
                width: '20%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'result',
        content: 'Result',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    }
];

const oestrogenReceptorCells: IGroupedTableCellProps[] = [
    {
        columnName: 'name',
        content: (
            <Typography>Oestrogen receptor</Typography>
        )
    },
    {
        columnName: 'percentage',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxEROperator"
    },
    {
        columnName: 'percentageValue',
        content: (
            <Input
                component={Numeric}
            />
        ),
        propertyPath: "sxERValue"
    },
    {
        columnName: 'intensity',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxERIntensity"
    },
    {
        columnName: 'result',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxERCat"
    },
];

const progesteroneReceptorCells = [
    {
        columnName: 'name',
        content: (
            <Typography>Progesterone receptor</Typography>
        )
    },
    {
        columnName: 'percentage',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxPROperator"
    },
    {
        columnName: 'percentageValue',
        content: (
            <Input
                component={Numeric}
            />
        ),
        propertyPath: "sxPRValue"
    },
    {
        columnName: 'intensity',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxPRIntensity"
    },
    {
        columnName: 'result',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxPRCat"
    },
];

const her2IhcCells: IGroupedTableCellProps[] = [
    {
        columnName: 'name',
        content: (
            <Typography>HER2 IHC</Typography>
        )
    },
    {
        columnName: 'percentage',
        content: (
            <></>
        )
    },
    {
        columnName: 'percentageValue',
        content: (
            <></>
        )
    },
    {
        columnName: 'intensity',
        content: (
            <></>
        )
    },
    {
        columnName: 'result',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxIHC"
    },
];

const her2IshCells: IGroupedTableCellProps[] = [
    {
        columnName: 'name',
        content: (
            <Typography>HER2 ISH</Typography>
        )
    },
    {
        columnName: 'percentage',
        content: (
            <></>
        )
    },
    {
        columnName: 'percentageValue',
        content: (
            <></>
        )
    },
    {
        columnName: 'intensity',
        content: (
            <></>
        )
    },
    {
        columnName: 'result',
        content: (
            <Input
                component={Select}
            />
        ),
        propertyPath: "sxISH"
    },
];

const HistopathologyForm: FunctionComponent<IHistopathologyFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedFields>
                    <GroupedField 
                        component={Numeric}
                        propertyPath="indexTumourSize"
                        numberFormat={{
                            isAllowed: (values : NumberFormatValues) => !values.value.includes("."),
                        }}
                        inputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography color="textSecondary">mm</Typography>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <GroupedField 
                        component={Numeric}
                        propertyPath="invasiveSize"
                        numberFormat={{
                            isAllowed: (values : NumberFormatValues) => !values.value.includes(".")
                        }}
                        inputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography color="textSecondary">mm</Typography>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <GroupedExpandableField 
                        component={Select}
                        propertyPath="dcis"
                        expandableContent={
                            <Box>
                                <Typography>
                                    Extensive DCIS (Extensive Intraductal Component (EIC))
                                </Typography>
                                <Typography>
                                    Extensive DCIS is defined as invasive carcinoma with the following three components:
                                </Typography>
                                <ul>
                                    <li>DCIS is present within the invasive tumour; and</li>
                                    <li>DCIS within invasive tumour comprises more than 25% of the invasive tumour volume; and</li>
                                    <li>DCIS exists beyond the margin of the invasive tumour.</li>
                                </ul>
                            </Box>
                        }
                    />

                    {/* <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={dcisReported}
                        subscription={{ value: true }}
                    >
                        <GroupedField 
                            component={Numeric}
                            propertyPath="dcisSize"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography color="textSecondary">mm</Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </CrfCondition> */}
                </GroupedFields>

                <GroupedFields>
                    <GroupedField 
                        component={SelectWithTooltip}
                        propertyPath="tumourHistopathologyType"
                        tooltipProps={{
                            arrow: true,
                            placement: "left",
                            title: "Invasive cancer NST should be reported as Invasive ductal"
                        }}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={tumourHistopathologyTypeSpecify}
                        subscription={{ value: true }}
                    >
                        <GroupedField 
                            component={Text}
                            propertyPath="tumourHistopathologyTypeSpecify"
                            label="Specify"
                        />
                    </CrfCondition>
                </GroupedFields>

                <GroupedField 
                    component={Select}
                    propertyPath="histologicGradeOfTumour"
                />
                
                <GroupedField 
                    component={ToggleButtonGroup}
                    propertyPath="lymphovascularInvasionPresent"
                />
                <GroupedExpandableField 
                    component={Select}
                    propertyPath="multifocality"
                    expandableContent={
                        <Box>
                            <Typography>
                                Where histopathology is unable to identify a ‘bridge’ of tumour tissue joining two or more apparent invasive cancer foci the following will be used to confirm unifocal disease:
                                <ul>
                                    <li>All foci must be of the same histology; and</li>
                                    <li>All foci must have the same hormone (ER and PR) and HER2 status.</li>
                                </ul>
                            </Typography>
                        </Box>
                    }
                />
                <GroupedField 
                    component={ToggleButtonGroup}
                    propertyPath="radialMarginPostSurgeryGT2"
                />

                <GroupedFields>
                    <GroupedField 
                        component={Select}
                        propertyPath="closestRadialMarginOperator"
                    />
                    <GroupedExpandableField 
                        component={Numeric}
                        propertyPath="closestRadialMarginValue"
                        label="Value"
                        expandableContent={
                            <Box>
                                <Typography>
                                    Radial margins are reported as superior, inferior, medial and lateral. Please report the microscopically measured closest radial margin.
                                </Typography>
                            </Box>
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography color="textSecondary">mm</Typography>
                                </InputAdornment>
                            )
                        }}
                    />
                </GroupedFields>

                <GroupedFields>
                    <GroupedField 
                        component={Select}
                        propertyPath="closestSuperficialMarginOperator"
                    />
                    <GroupedExpandableField 
                        component={Numeric}
                        propertyPath="closestSuperficialMarginValue"
                        label="Value"
                        expandableContent={
                            <Box>
                                <Typography>
                                    Please report the microscopically measured closest superficial or deep margin.
                                </Typography>
                            </Box>
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography color="textSecondary">mm</Typography>
                                </InputAdornment>
                            )
                        }}
                    />
                </GroupedFields>
                
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={closestSuperficialMarginLessThanTwo}
                    subscription={{ value: true }}
                >
                    <GroupedField 
                        component={ToggleButtonGroup}
                        propertyPath="breastTissueRespectivelyRemoved"
                    />
                </CrfCondition>

                
                <GroupedFields>
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={ki67AssessmentDone}
                        subscription={{ value: true }}
                    >
                        <GroupedField 
                            component={Numeric}
                            propertyPath="ki67AssessmentResult"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography color="textSecondary">%</Typography>
                                    </InputAdornment>
                                )
                            }}
                            // slotProps={{
                            //     input: {
                            //         endAdornment: (
                            //             <InputAdornment position="end">
                            //                 %
                            //             </InputAdornment>
                            //         )
                            //     }
                            // }}
                        />
                    </CrfCondition>

                    <GroupedField 
                        component={Checkbox}
                        propertyPath="ki67NotDone"
                        LabelProps={{
                            label: <></>
                        }}
                        formInputOptions={{
                            labelMode: InputOptionMode.UseCustom
                        }}
                        label='Not Done'
                    />
                </GroupedFields>

                <GroupedField 
                    component={ToggleButtonGroup}
                    propertyPath="hormoneReceptorTested"
                />

                <StyledSubheading
                    variant="h2"
                >
                    Hormone Receptor Status
                </StyledSubheading>

                <GroupedTable
                    headers={hormoneReceptorStatusHeader}
                >
                    <GroupedTableRow 
                        cells={oestrogenReceptorCells}
                    />
                    <GroupedTableRow 
                        cells={progesteroneReceptorCells}
                    />
                    <GroupedTableRow 
                        cells={her2IhcCells}
                    />
                    <GroupedTableRow 
                        cells={her2IshCells}
                    />
                </GroupedTable>

                <SourceDocuments />
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default HistopathologyForm;
